import React from "react";
import { Link } from "react-router-dom";

function TuxedoTutorial() {
  return (
    <div style={{ padding: "20px", maxWidth: "900px", margin: "auto", lineHeight: "1.7", textAlign: "justify" }}>
      <h1 style={{ textAlign: "center" }}>BEA TUXEDO Architecture</h1>
      <p>
        BEA TUXEDO is primarily architected for the Unix operating system, which typically runs on symmetric multi-processor (SMP) machines. 
        Unix is a general-purpose operating system, and does not provide the services required for support of transaction processing applications 
        (for example - transaction management, load-balancing, process restart). BEA TUXEDO provides such services internally, thereby providing 
        an OLTP environment for applications on Unix. An important design goal of BEA TUXEDO is to be portable across Unix implementations 
        and underlying hardware platforms.
      </p>

      <h2>Aspects of the BEA TUXEDO architecture which are pertinent to this discussion are as follows:</h2>
      <ul>
        <li>Shared Memory</li>
        <li>Interprocess Messages</li>
        <li>Transaction Management</li>
        <li>Process Management</li>
        <li>Load Balancing</li>
      </ul>

      <h3>Shared Memory</h3>
      <p>
        SMP machines provide a single physical memory-space which is shared by all CPUs in the machine. Memory may be shared between 
        processes regardless of the CPU executing the process. BEA TUXEDO uses shared memory extensively for data-structures, such as:
      </p>
      <p>
        <strong>Bulletin board (BB):</strong> Contains current configuration and processing state information. Some information in the BB 
        is global and is replicated on every machine in the TUXEDO application (for example, the names and locations of all servers offering 
        a particular service). Other information is local, and visible only within the SMP on which the BB exists (for example, the actual 
        number and type of client requests currently waiting on a local server request queue). The BB is accessed by clients, servers, and 
        the "bulletin board liaison" (BBL) process.
      </p>
      <p>
        <strong>Global transaction table (GTT):</strong> Contains current transaction state information. The GTT is accessed by 
        "transaction management server" (TMS) processes.
      </p>
      <p>
        A single SMP equals a TUXEDO "machine". The BBL is responsible for updating the global information in the BB. There is a BBL on every 
        machine in the application. There is also a single "master" BBL process (the "distinguished" BBL (DBBL)), which is responsible for 
        keeping all the replicated BBs synchronized. Process access to shared-memory is controlled via the use of a combination of user 
        and Unix semaphores.
      </p>

      <h3>Interprocess Messages</h3>
      <p>
        Client-server communication between processes on the same machine is effected via use of Unix messages and queues. Because Unix 
        messages cannot span machines, communication between processes on different SMP machines is via network transport mechanisms 
        (for example, TCP/IP). A TUXEDO system process (the "Bridge" process) exists on every machine in the application. Bridges are 
        TUXEDO system servers whose function is to route requests from local clients to remote machines, and to route requests from remote 
        machines to local servers (Bridges communicate with each other across the network). Since the scope of a Unix message queue is local 
        to that Unix OS instance (that is, to a single SMP machine), all the server processes reading from a particular queue must be local 
        to that machine.
      </p>

      <h3>Transaction Management</h3>
      <p>
        BEA TUXEDO relies on the use of external resource managers for application data-access (for example, RDBMSs). BEA TUXEDO and external 
        RMs cooperate to perform global transaction co-ordination (via the X/Open XA interface). TUXEDO servers may access one or more external 
        RMs as part of a single global transaction. TUXEDO is responsible for coordinating each such RM, so that they all commit, or all roll-back 
        their branch of the transaction (transaction atomicity). This is achieved via the TMS system server process. There is a TMS process for 
        each server group defined in the application. Each TMS manages that portion of the transaction branch executed by servers in its group. 
        One TMS acts as the transaction root. This TMS "knows" which other server groups were involved in the transaction and broadcasts two-phase 
        commit messages to the TMSs in those groups. The subordinate TMSs coordinate with their RM via the XA interface and return the reply to the root TMS.
      </p>
      <p>
        The root TMS in each TUXEDO domain, plus each RM involved in the transaction, write a synchronous transaction log record during transaction 
        commitment. There is one TUXEDO physical log-file per machine, plus one physical log-file for each RM instance.
      </p>

      <h3>Process Management</h3>
      <p>
        One of the key functions of a TP monitor is to provide fast, reliable access to business applications to many clients simultaneously. 
        In TUXEDO, this is done by making server processes available which serially execute client requests (TUXEDO servers are single-threaded). 
        To ensure server availability, a periodic polling mechanism is used to check whether all server processes are up and running (the periodicity 
        of this check is configurable). Each server instance is "pinged" by its local BBL. If a server has failed, TUXEDO will clean-up and restart 
        the offending server process. If a whole machine fails, the servers on that machine become unavailable until either the machine is manually 
        recovered, or the servers are migrated administratively to another machine. And there is at least one server group for every RM "instance" 
        configured per application. Servers within a server group may only access a single RM instance. e.g. an Oracle instance, or an Informix instance. 
        Servers in different server groups may access the same or different RM instances.
      </p>

      <h3>Load Balancing</h3>
      <p>
        Fast-response time to clients is achieved via parallelism: several identical server process instances are started, and client requests 
        are distributed evenly between them. If these instances are configured as a multi-server single-queue (MSSQ) set, then load balancing 
        is achieved between the servers in the MSSQ-set, since only an idle server will read a request off the queue. If only a single MSSQ-set 
        (or single server) is configured to offer a particular service, then no further load-balancing is required. If however more than one MSSQ-set 
        (or single server) offers a service, then requests must be distributed evenly between the server queues. Two flavors of such load-balancing 
        pertain, within a machine (the client and all servers offering the service are located on the same machine), and across machines (the client 
        and servers offering the service are located on different machines). In both cases, the target server is selected at the client. Within a 
        machine, the BB contains current statistics as to the load on each server queue, and hence exactly the least-loaded server queue can be selected. 
        This is not the case for servers on remote machines (the local site cannot reasonably "know" the load on remote servers; there is no global view). 
        So, in this case a simple, weighted, "round-robin" algorithm is used to toggle between servers on different machines.
      </p>

      <h2>History of Tuxedo</h2>
      <ul>
         <li><Link to="/tuxedo-history">History of Tuxedo</Link></li>
        <li><Link to="/tuxedo-what-is">What is Tuxedo?</Link></li>
        <li><Link to="/tuxedo-components">Components of Tuxedo</Link></li>
        <li><Link to="/tuxedo-core-services">Core Services of Tuxedo</Link></li>
        <li><Link to="/tuxedo-atmi-highlights">ATMI Highlights</Link></li>
        <li><Link to="/tuxedo-rich-messaging">Rich Messaging with Tuxedo</Link></li>
        <li><Link to="/tuxedo-atmi-tx-verbs">ATMI and Tx Verbs</Link></li>
        <li><Link to="/tuxedo-buffer-management">Tuxedo Buffer Management</Link></li>
        <li><Link to="/tuxedo-admin-advantages">Simpler Administration Advantages</Link></li>
        <li><Link to="/tuxedo-config-management">Configuration and Management</Link></li>
        <li><Link to="/tuxedo-domain-support">Domain Support</Link></li>
        <li><Link to="/tuxedo-dtp-scenarios">Heterogeneous DTP - Scenarios</Link></li>
        <li><Link to="/tuxedo-domain-architecture">Domain Architecture</Link></li>
        <li><Link to="/tuxedo-q-components">Q Description and Q Components</Link></li>
        <li><Link to="/tuxedo-event-broker">Event Broker</Link></li>
        <li><Link to="/tuxedo-mib">Management Information Base (MIB)</Link></li>
        <li><Link to="/tuxedo-jolt">Jolt and Jolt Components</Link></li>
        <li><Link to="/tuxedo-weblogic">WebLogic server and Tuxedo connector</Link></li>
      </ul>

      <h2>Tuxedo Basics</h2>
      <ul>
      <li><Link to="/tuxedo-build-application">How to Build a simple Tuxedo Application</Link></li>
        <li>Typed Buffer in Tuxedo and Basics ATMI for Buffer Allocation</li>
        <li>Basic ATMI Functions in Tuxedo:
          <ul>
            <li>Asynchronous Operations</li>
            <li>Unsolicited Message Handling</li>
            <li>VIEW/VIEW32 Buffers</li>
            <li>FML/FML32 Buffers</li>
            <li>Conversational Client Communication</li>
            <li>Publish/Subscribe (Event Broker)</li>
            <li>Security</li>
            <li>Transaction Management</li>
            <li>Working with Queue</li>
            <li>Network and Workstation Clients</li>
          </ul>
        </li>
        <li>Inter domain Communications</li>
        <li>WebLogic Tuxedo Connector</li>
        <li>Jolt</li>
      </ul>
    </div>
  );
}

export default TuxedoTutorial;
